import sortBy from 'lodash/sortBy'
import uniqueId from 'lodash/uniqueId'

function formatMenus(payload) {
  // generate the same payload structure as `RECEIVE_MENUS` to
  // avoid changing how front-end components render the data
  return payload.results.map((menu, menuIndex) => ({
    id: parseInt(uniqueId()),
    important: menu.important || false,
    name: menu.data.text.trim() || menu.name.trim(),
    color: menu.data.mainNavTextColor,
    placement: menu.data.placement,
    platform: menu.data.platform,
    position: menuIndex,
    images: menu.data.images || [],
    url: menu.data.url,
    submenus:
      menu.data.sections?.map((section, sectionIndex) => ({
        id: parseInt(uniqueId()),
        important: section.important || false,
        name: section.text,
        placement: section.placement,
        platform: section.platform,
        position: sectionIndex,
        submenus: (section.links || []).map((link, submenuIndex) => ({
          id: parseInt(uniqueId()),
          important: link.important || false,
          name: link.text,
          placement: link.placement,
          platform: link.platform,
          position: submenuIndex,
          submenus: [],
          url: link.url,
          color: link.color,
        })),
        target_type: section.targetType || '',
        url: section.url,
      })) || [],
    target_type: menu.data.targetType || '',
  }))
}

function formatMenusV2(payload) {
  return payload.results.map(menu => ({
    id: parseInt(uniqueId()),
    important: menu.important || false,
    name: menu.data.text.trim() || menu.name.trim(),
    url: menu.data.url,
    platform: menu.data.platform,
    categories: menu.data.categories?.map(category => ({
      text: category.text,
      permalink: category.permalink,
      color: category.color,
      platform: category.platform,
      sections: category.sections?.map(section => ({
        title: section.title,
        links: section.links?.map(link => ({
          text: link.text,
          permalink: link.permalink,
          image: link.image,
        })),
      })),
      images: category.images,
    })),
  }))
}

export default function menus(state = {}, { type, payload }) {
  switch (type) {
    case 'RECEIVE_BUILDER_MENUS':
      if (!payload.results.length) {
        return { ...state }
      }

      const menus = formatMenus(payload)
      return {
        ...state,
        main: menus,
      }

    case 'RECEIVE_BUILDER_MENUS_V2':
      if (!payload.results.length) {
        return { ...state }
      }

      const menusV2 = formatMenusV2(payload)
      return {
        ...state,
        main: menusV2,
      }

    case 'RECEIVE_SIDENAV':
      if (!payload.results.length) {
        return { ...state }
      }

      const sidenav = formatMenus(payload)
      return { ...state, sidenav }

    default:
      return state
  }
}

export const MenuItemFilter = {
  mobile: menu => /mobile/.test(menu.platform),
  desktop: menu => /desktop/.test(menu.platform),
  'desktop-mobile': menu => /desktop|mobile/.test(menu.platform),
  top: menu => /top/.test(menu.placement),
  side: menu => /side/.test(menu.placement),
  'top-side': menu => /top|side/.test(menu.placement),
}

export function getFilteredMenusRecursively(
  state,
  platform = 'desktop-mobile',
  placement = 'top-side',
) {
  const menus = state.filter(menu => {
    return MenuItemFilter[platform](menu) && MenuItemFilter[placement](menu)
  })

  return sortBy(menus, 'position').map(menu => ({
    ...menu,
    submenus: getFilteredMenusRecursively(menu.submenus, platform, placement),
  }))
}

export function getMobileMenus(state, position = 'top-side') {
  return getFilteredMenusRecursively(state, 'mobile', position).filter(menu =>
    // TODO: Remove this when the menu.platform is fully utilized
    // rather than hardcoding the value here.
    ['Men', 'Women'].includes(menu.name),
  )
}

export function getCollapsingMobileMenus(state, user) {
  const gender = (() => {
    if (user) {
      return user.gender === 'male' ? 'men' : 'women'
    } else {
      return 'women'
    }
  })()

  const sortByGender = menu => {
    return menu.name.toUpperCase() === gender.toUpperCase() ? -1 : 1
  }

  const menus = getFilteredMenusRecursively(state, 'mobile', 'side').filter(menu =>
    ['Men', 'Women', 'Denim'].includes(menu.name),
  )

  return menus
    .map(menu => {
      if (menu.name === 'Denim') {
        menu.submenus.sort(sortByGender)
      }
      return menu
    })
    .sort(sortByGender)
}

export function getGenderSideMenusForDesktop(state, gender) {
  const genderMappings = {
    female: 'Women',
    male: 'Men',
    unisex: 'Women',
  }

  return getFilteredMenusRecursively(state, 'desktop', 'side').find(
    menu => menu.name === genderMappings[gender],
  )
}
